import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RQAPICallWithData } from 'store/APIClient';
import { FilterModel } from 'ag-grid-community';

type GridFilters = {
    exercice: number;
    analyseGridFilters?: FilterModel | null;
    projectionsGridFilters?: FilterModel | null;
};

const fetchGridFilters = async (exercice: number): Promise<GridFilters> => {
    const response = await RQAPICallWithData<GridFilters>({
        endpoint: `budget_user_settings/exercice/grid_filters/${exercice}`,
        method: 'GET',
    });

    return response.content.data || {};
};

const GRID_FILTERS_BASE_KEY = `grid_filters_settings_query_`;

/**
 * Hooks to get user grid filters for Analyse and Projections Grid.
 */
export const useGetGridFilters = (
    exercice: number,
): {
    gridFilters: GridFilters;
    isLoading: boolean;
} => {
    const { data, isLoading } = useQuery({
        queryKey: [`${GRID_FILTERS_BASE_KEY}${exercice}`],
        queryFn: async () => await fetchGridFilters(exercice),
    });
    const gridFilters = data ?? {
        exercice: exercice,
        analyseGridFilters: {},
        projectionsGridFilters: {},
    };
    return { gridFilters, isLoading };
};

/**
 * Hooks to patch user grid filters for Analyse and Projections Grid.
 */
export const useMutateGridFilters = (
    exercice: number,
): {
    mutate: (arg1: GridFilters) => void;
} => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: (body: GridFilters) =>
            RQAPICallWithData<GridFilters>({
                endpoint: 'budget_user_settings/exercice/grid_filters',
                method: 'PATCH',
                body,
            }),

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [`${GRID_FILTERS_BASE_KEY}${exercice}`],
            });
        },
    });
    return mutation;
};
